import {
  Button,
  Flex,
  Box,
  Text,
  FormControl,
  FormLabel,
  CheckboxGroup,
  Input,
  FormErrorMessage,
  Tooltip,
} from '@chakra-ui/react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { object, string } from 'yup';
import { FormSelect } from '@/design-system/select';
import { UserOption, UserMultiValue } from '@/components/select';
import { Schedule } from './schedule';
import { FormCheckbox } from './form-checkbox';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import { useGetOpenJobsQuery } from '@/store/query/ats';
import { useSelector } from 'react-redux';
import { outreachFunctions } from '@/constants/outreach-functions';
import { Link } from 'react-router-dom';
import {
  selectOutreachUsers,
  selectOutreachusersIsLoading,
} from '@/store/users/selectors';
import { useGetRecruitersQuery } from '@/store/query/recruiters';
import { useKFlags } from '@/hooks/use-k-flags';

const useOutreachFormInitialValue = (outreach = null, type) => {
  const currUser = useSelector(selectCurrUser);
  const defaultUser = {
    ...currUser,
    name: `${currUser?.first_name} ${currUser?.last_name || ''}`,
  };
  if (!outreach) {
    return {
      title: outreach,
      collaborators: [defaultUser],
      atsJobId: '',
      trackClicks: true,
      trackOpens: true,
      schedule: {},
      imageKey: '',
      tags: [],
    };
  }
  const {
    title,
    collaborators,
    schedule,
    track_clicks,
    track_opens,
    ats_job,
    tag_names,
    image_key,
  } = outreach;
  return {
    title: outreach && type === 'clone' ? `[Copy of] ${title}` : title,
    collaborators: collaborators?.length ? collaborators : [defaultUser],
    atsJobId: ats_job,
    trackClicks: track_clicks,
    trackOpens: track_opens,
    schedule,
    imageKey: image_key,
    tags: tag_names?.map((tag) => ({ label: tag, value: tag })) || [],
  };
};

export const OutreachForm = ({
  buttonLabel = 'Create Flow',
  isLoading = false,
  outreach = null,
  onSubmit = () => {},
  type = 'new',
}) => {
  const initialValues = useOutreachFormInitialValue(outreach, type);
  const { feHmChanges, feSettingsNav, ats_enabled } = useKFlags();
  const isOutreachUsersLoading = useSelector(selectOutreachusersIsLoading);
  const outreachUsers = useSelector(selectOutreachUsers);
  const { data: users, isFetching: isUsersLoading } = useGetRecruitersQuery();
  const { data: jobs, isFetching: isJobsLoading } = useGetOpenJobsQuery();
  const currUser = useSelector(selectCurrUser);
  const { tFlowTimeZone } = useKFlags();

  const isATSEnabled = () => {
    const { role } = currUser;
    return !(role === 'recruiter' || role === 'hiring_manager');
  };

  const outreachSchema = object().shape({
    title: string()
      .nullable()
      .required('Flow name is required')
      .max(50, 'Flow name cannot exceed 50 characters'),
    schedule: object().isEmptyObject({ message: 'Schedule is required' }),
    time_zone: tFlowTimeZone
      ? string().nullable().required('Timezone is required')
      : string().nullable(),
  });

  return (
    <Formik
      validateOnBlur={false}
      initialValues={initialValues}
      validationSchema={outreachSchema}
      onSubmit={onSubmit}
    >
      <Form noValidate style={{ height: '100%' }}>
        <Flex h="100%">
          <Flex
            w="100%"
            borderRightWidth="1px"
            borderColor="border-color"
            flexDir="column"
            height="100%"
          >
            <Box
              p="24px"
              overflow="auto"
              borderBottomWidth="1px"
              borderColor="border-color"
              flex="1"
            >
              <Field name="title">
                {({ field, form }) => (
                  <FormControl
                    mb="16px"
                    isRequired
                    isInvalid={form.errors.title && form.touched.title}
                  >
                    <FormLabel
                      fontWeight="semiBold"
                      fontSize="14px"
                      htmlFor="title"
                    >
                      Name of the Flow
                    </FormLabel>
                    <Input
                      {...field}
                      id="title"
                      placeholder="Flow Name"
                      data-tracking-id="outreach-create-name"
                      data-test-id="outreach-create-name"
                      maxLength={50}
                    />
                    <FormErrorMessage>
                      <ErrorMessage name="title" />
                    </FormErrorMessage>
                  </FormControl>
                )}
              </Field>

              <Box mb="16px" data-tracking-id="outreach-create-collaborators">
                <FormLabel
                  fontWeight="semiBold"
                  fontSize="14px"
                  htmlFor="collaborators"
                >
                  Add Recruiter / Sourcer/ Hiring Manager
                </FormLabel>
                <FormSelect
                  id="collaborators"
                  options={feHmChanges ? outreachUsers : users}
                  isMulti={true}
                  placeholder="Select the recruiter"
                  isLoading={
                    feHmChanges ? isOutreachUsersLoading : isUsersLoading
                  }
                  getOptionLabel={(user) => user.name}
                  getOptionValue={(user) => user.id}
                  components={{
                    Option: UserOption,
                    MultiValueLabel: UserMultiValue,
                  }}
                />
              </Box>

              {!isJobsLoading && jobs?.ats_jobs?.length > 0 ? (
                <Box mb="16px" data-tracking-id="outreach-create-ats-jobs">
                  <FormLabel
                    fontWeight="semiBold"
                    fontSize="14px"
                    htmlFor="atsJobId"
                  >
                    Applicant Tracking System
                  </FormLabel>
                  <FormSelect
                    id="atsJobId"
                    options={jobs?.ats_jobs?.map((job) => ({
                      ...job,
                      value: job?.id,
                      label: job?.name,
                    }))}
                    placeholder="Select the ATS job"
                    isLoading={isJobsLoading}
                    getOptionLabel={(job) => job.label}
                    getOptionValue={(job) => job.value}
                  />
                </Box>
              ) : (
                <Box mb="16px" cursor="not-allowed">
                  <FormLabel
                    fontWeight="semiBold"
                    fontSize="14px"
                    htmlFor="atsJobId"
                  >
                    Applicant Tracking System
                  </FormLabel>
                  <FormSelect
                    id="atsJobId"
                    options={jobs?.ats_jobs?.map((job) => ({
                      ...job,
                      value: job?.id,
                      label: job?.name,
                    }))}
                    placeholder="Select the ATS job"
                    isLoading={isJobsLoading}
                    isDisabled={true}
                    getOptionLabel={(job) => job.label}
                    getOptionValue={(job) => job.value}
                  />
                  {isATSEnabled() && (
                    <Text variant="muted" mt="10px">
                      No ATS integration found.
                      <Link
                        to={
                          ats_enabled
                            ? '/settings/profile-integration'
                            : feSettingsNav
                            ? '/settings/org/integrations'
                            : '/settings/org#integrations'
                        }
                      >
                        <Text
                          color="#0043CE"
                          size="sm-bold"
                          as="span"
                          textDecor="underline"
                          p="0 4px"
                        >
                          Click here
                        </Text>
                      </Link>
                      to integrate your ATS with Kula.
                    </Text>
                  )}
                </Box>
              )}

              <Box mb="16px" data-tracking-id="outreach-create-department">
                <FormLabel fontWeight="semiBold" fontSize="14px" htmlFor="tags">
                  Departments
                </FormLabel>
                <FormSelect
                  id="tags"
                  options={outreachFunctions}
                  placeholder="Select the department"
                  isMulti={true}
                />
              </Box>

              <Field name="schedule">
                {({ form }) => (
                  <FormControl
                    mb="16px"
                    isRequired
                    isInvalid={form.errors.schedule && form.touched.schedule}
                  >
                    <Flex>
                      <FormLabel
                        fontWeight="semiBold"
                        fontSize="16px"
                        htmlFor="schedule"
                        mt="20px"
                        mb="8px"
                      >
                        Schedule your flow
                      </FormLabel>
                    </Flex>
                    {tFlowTimeZone && (
                      <Text
                        mb="20px"
                        data-tracking-id="outreach-create-schedule"
                      >
                        The timezone is based on the sender's profile but you
                        can choose any different timezone you want. <br />
                        <b color="#161616">Note:</b> All automatic LinkedIn
                        tasks will run only during this timeframe unless they
                        are overdue. You can initiate overdue automatic tasks
                        from the Tasks/Dashboard page anytime.
                      </Text>
                    )}

                    {!tFlowTimeZone && (
                      <Text mb="20px" color="#8D8D8D">
                        The timezone is based on the sender's profile. You can
                        edit your profile's timezone under settings.
                      </Text>
                    )}
                    <Schedule isNew={!Boolean(outreach)} fieldName="schedule" />
                    <FormErrorMessage>
                      <ErrorMessage name="schedule" />
                    </FormErrorMessage>
                  </FormControl>
                )}
              </Field>

              <Box my="20px" data-tracking-id="outreach-create-tracking">
                <FormControl>
                  <FormLabel
                    fontSize="16px"
                    fontWeight="semiBold"
                    mt="20px"
                    mb="8px"
                  >
                    Tracking
                  </FormLabel>
                  <Text variant="muted" mb="20px">
                    Track performance of candidate reach-out Flows
                  </Text>
                  <CheckboxGroup>
                    <FormCheckbox
                      selected={true}
                      fieldName="trackOpens"
                      mr="12px"
                    >
                      <Text variant="muted">Track opens</Text>
                    </FormCheckbox>
                    <FormCheckbox selected={true} fieldName="trackClicks">
                      <Text variant="muted">Track clicks</Text>
                    </FormCheckbox>
                  </CheckboxGroup>
                </FormControl>
              </Box>
            </Box>
            <Flex justifyContent="right" h="80px" alignItems="center" px="24px">
              {(outreach?.status === 'archived' ||
                outreach?.status === 'completed') &&
              buttonLabel === 'Update Flow' ? (
                <Tooltip
                  label="The Flow is either complete or been archived"
                  shouldWrapChildren
                  hasArrow
                  placement="top-end"
                  bg="#161616"
                  borderRadius="3px"
                  arrowSize={8}
                  mb="2"
                >
                  <Button type="submit" isLoading={isLoading} isDisabled>
                    {buttonLabel}
                  </Button>
                </Tooltip>
              ) : (
                <Button
                  type="submit"
                  isLoading={isLoading}
                  data-tracking-id="outreach-create-submit"
                  data-test-id="outreach-create-submit"
                >
                  {buttonLabel}
                </Button>
              )}
            </Flex>
          </Flex>
        </Flex>
      </Form>
    </Formik>
  );
};
